import { useEffect, useRef, useCallback } from 'preact/hooks';

export interface ArSessionTrackingConfig {
  isActive: boolean;
  hasExited: boolean;
  contentId: string | null;
  contentName: string | null;
  sendEvent: (name: string, params?: Record<string, any>) => void;
}

/**
 * Track AR session duration with visibility awareness
 * - Pauses timer when app is backgrounded
 * - Fires ar_session_start/end events
 */
export function useArSessionTracking({
  isActive,
  hasExited,
  contentId,
  contentName,
  sendEvent,
}: ArSessionTrackingConfig) {
  const startTimeRef = useRef(0);
  const activeTimeRef = useRef(0);
  const sessionSentRef = useRef(false);
  const wasActiveRef = useRef(false);

  const sendSessionEnd = useCallback(() => {
    if (sessionSentRef.current || !contentId || startTimeRef.current === 0) return;
    
    // Add remaining active time
    if (document.visibilityState === 'visible' && startTimeRef.current > 0) {
      activeTimeRef.current += Date.now() - startTimeRef.current;
    }
    
    sendEvent('ar_session_end', {
      content_id: contentId,
      content_name: contentName,
      duration_seconds: Math.round(activeTimeRef.current / 1000),
      transport_type: 'beacon',
    });
    
    sessionSentRef.current = true;
    startTimeRef.current = 0;
    activeTimeRef.current = 0;
  }, [contentId, contentName, sendEvent]);

  // Track session start
  useEffect(() => {
    if (isActive && contentId && !wasActiveRef.current) {
      startTimeRef.current = Date.now();
      activeTimeRef.current = 0;
      sessionSentRef.current = false;
      wasActiveRef.current = true;
      
      sendEvent('ar_session_start', {
        content_id: contentId,
        content_name: contentName,
      });
    }
    
    if (!isActive && wasActiveRef.current) {
      // Fall back to an end event when the session becomes inactive without
      // a clean XR exit signal or unload event.
      sendSessionEnd();
      wasActiveRef.current = false;
    }
  }, [isActive, contentId, contentName, sendEvent, sendSessionEnd]);

  // Track session end (normal exit)
  useEffect(() => {
    if (hasExited) sendSessionEnd();
  }, [hasExited, sendSessionEnd]);

  // Pause/resume on visibility change
  useEffect(() => {
    if (!isActive) return;
    
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (startTimeRef.current > 0) {
          activeTimeRef.current += Date.now() - startTimeRef.current;
          startTimeRef.current = 0;
        }
      } else if (!sessionSentRef.current) {
        startTimeRef.current = Date.now();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [isActive]);

  // Handle page unload
  useEffect(() => {
    if (!isActive) return;
    
    window.addEventListener('pagehide', sendSessionEnd);
    return () => window.removeEventListener('pagehide', sendSessionEnd);
  }, [isActive, sendSessionEnd]);

  return { endSession: sendSessionEnd };
}
